import React from 'react';
import { Check, Quote, Star } from 'lucide-react';
import { mockData } from '../../mock';
import { Reveal, SectionHeader, slugify } from './Shared';

const COACH_IMG = 'https://customer-assets.emergentagent.com/job_ace-padel/artifacts/kwbpy2ur_Screenshot%202025-11-03%20161416.png';

export const NlCoaching = ({ onEnquire }) => (
  <section id="coaching" data-testid="nl-coaching" className="nl-section relative">
    <div className="absolute inset-0 nl-grid-bg opacity-60" />
    <div className="nl-container relative">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <SectionHeader
            label="Coaching"
            title={<>Play better,<br />faster.</>}
            lead="Learn, grow, and play with certified padel coaches dedicated to helping you improve your skills, boost confidence, and build a lifelong passion for the game."
          />
          <Reveal delay={2} className="mt-8 relative rounded-[1.5rem] overflow-hidden border border-white/10 aspect-[16/10]">
            <img src={COACH_IMG} alt="Coaching session at space#" className="nl-img-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D11]/80 to-transparent" />
            <div className="absolute bottom-5 left-5 nl-label">Certified coaches · All levels</div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
          {mockData.coachingOptions.map((opt, i) => {
            const hot = i === 1;
            return (
              <Reveal key={opt.title} delay={i} className={`nl-card p-6 flex flex-col ${hot ? 'border-amber-500/40 bg-amber-500/5 sm:-translate-y-4' : ''}`}>
                <div data-testid={`nl-coaching-card-${slugify(opt.title)}`} className="flex flex-col h-full">
                  {hot && <span className="nl-label !text-[0.6rem] mb-3">Most popular</span>}
                  <h3 className="text-xl font-extrabold leading-tight">{opt.title}</h3>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{opt.description}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {opt.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => onEnquire(opt.title)}
                    data-testid={`nl-coaching-enquire-${slugify(opt.title)}`}
                    className={`nl-btn nl-btn-sm mt-6 w-full ${hot ? 'nl-btn-primary' : 'nl-btn-ghost'}`}
                  >
                    Enquire
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

const TESTIMONIALS = [
  { quote: 'Walked in never having held a padel racket. Two sessions later I was hooked. The coaches make it ridiculously easy to start.', name: 'Arjun M.', tag: 'Plays at Ozone Avenue', rating: 5 },
  { quote: 'Best courts in Kochi, hands down. Lighting, surface, glass — everything feels pro. Our Friday group has doubled in size.', name: 'Neha S.', tag: 'Group clinic regular', rating: 5 },
  { quote: 'We booked a corporate program for our team and it was the most fun offsite we have ever had. Zero experience needed.', name: 'Rahul K.', tag: 'Corporate program', rating: 5 },
];

export const NlTestimonials = () => (
  <section id="testimonials" data-testid="nl-testimonials" className="nl-section pt-0">
    <div className="nl-container">
      <SectionHeader label="Community" title="Players talk." align="center" />
      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i} className={`nl-card p-8 flex flex-col ${i === 1 ? 'md:-translate-y-6' : ''}`}>
            <Quote className="w-8 h-8 text-amber-500/60" />
            <p className="mt-5 text-slate-200 leading-relaxed flex-1">"{t.quote}"</p>
            <div className="mt-6 flex items-center justify-between">
              <div>
                <div className="font-bold">{t.name}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">{t.tag}</div>
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: t.rating }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />)}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const NlWhyChoose = () => (
  <section data-testid="nl-why-choose" className="nl-section pt-0">
    <div className="nl-container">
      <div className="nl-card p-8 md:p-12 grid lg:grid-cols-12 gap-10 bg-gradient-to-br from-[#131720] to-[#0B0D11]">
        <div className="lg:col-span-4">
          <SectionHeader label="Why space#" title="More than a court." />
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-8">
          {mockData.benefits.map((b, i) => (
            <Reveal key={b.title} delay={i} className="flex flex-col gap-4">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 [&_svg]:w-7 [&_svg]:h-7">{b.icon}</div>
              <h4 className="font-bold text-lg">{b.title}</h4>
              <p className="text-slate-400 text-sm leading-relaxed">{b.description}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
